import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";

import { ChatRecord } from "../ChatRecord";

interface UseChatsForUserOptions {
  pageToken?: string;
}

/**
 * React Query hook for fetching a paginated list of chats for the current user.
 *
 * @param options - Optional configuration including pageToken for pagination
 * @returns Query object with chat list data, loading state, and error state
 *
 * @example
 * ```tsx
 * const chatsQuery = useChatsForUser();
 *
 * if (chatsQuery.isLoading) {
 *   return <div>Loading chats...</div>;
 * }
 *
 * if (chatsQuery.error) {
 *   return <div>Error loading chats</div>;
 * }
 *
 * return (
 *   <div>
 *     {chatsQuery.data?.items.map(chat => (
 *       <div key={chat.chat_id}>{chat.title}</div>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useChatsForUser(options?: UseChatsForUserOptions) {
  const { chatService } = useDomainServices();

  return useQuery<PaginatedResponse<ChatRecord>, Error>({
    queryKey: ["chatsForUser", options?.pageToken],
    queryFn: ({ signal }) => {
      return chatService.getChatsForUser({
        pageToken: options?.pageToken,
        abortSignal: signal,
      });
    },
    // Keep chat list relatively fresh
    staleTime: 30000, // 30 seconds
  });
}
